/**
 * Helpers for purging cookies that live at non-`/` paths.
 *
 * Background — the silent-shadow bug:
 *   Legacy callsites in this codebase used raw `document.cookie = '<name>=...'`
 *   without specifying a path. Browsers default such cookies to the current
 *   URL's directory and persist them there. Those path-scoped residues are
 *   NOT removed by `path=/` deletes, and `getCookie()` (which reads
 *   `document.cookie.split(';')` in browser order) returns the longest-path
 *   match first — so the stale value silently shadows a freshly-set
 *   `path=/` cookie (RFC 6265 §5.4).
 *
 *   The concrete failure mode: user is on `/sales/orders/123` when the token
 *   expires. A legacy callsite wrote `token` at `path=/sales/orders` long ago.
 *   Logout's `path=/` delete cannot reach it. Login from `/login` writes a
 *   new `path=/` cookie. User navigates back to `/sales/orders/123` via the
 *   `targetUrl` cookie. The next request includes BOTH cookies; the backend
 *   sees the stale one first → 401 → loop. Only "Clear site data" recovers.
 *
 * The fix — walk every plausible path and issue a delete at each. Browsers
 * cannot enumerate cookies from a different path than the current page's
 * URL, so we cannot DISCOVER residues; we can only TARGET likely paths and
 * delete there. We target:
 *   - every parent of `window.location.pathname`
 *   - every parent of the `targetUrl` cookie (where the user was when their
 *     session expired)
 *   - any explicit `extraPaths` passed by the caller
 */

/**
 * Enumerate every parent path of `path`, root first.
 *
 *   '/'                          → ['/']
 *   '/login'                     → ['/', '/login']
 *   '/sales/orders/123'          → ['/', '/sales', '/sales/orders', '/sales/orders/123']
 *   '/login?reason=session-...'  → ['/', '/login']     (query stripped)
 *   ''                           → ['/']               (empty falls back to root)
 */
export function getAllPathSegments(path: string): string[] {
  if (typeof path !== 'string' || path.length === 0) return ['/']
  const cleanPath = path.split('?')[0]!.split('#')[0]!
  const segments = cleanPath.split('/').filter(Boolean)
  if (segments.length === 0) return ['/']
  const result: string[] = ['/']
  for (let i = 0; i < segments.length; i++) {
    result.push('/' + segments.slice(0, i + 1).join('/'))
  }
  return result
}

/**
 * Delete a cookie at every path it might have been written at: the root,
 * every parent of the current URL, and every parent of `extraPaths`
 * (typically `getCookie('targetUrl')`).
 *
 * SSR-safe (no-op when `window` is undefined).
 */
export function deleteCookieAtAllPaths(name: string, extraPaths: readonly string[] = []): void {
  if (typeof name !== 'string' || name.length === 0) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const paths = new Set<string>()
  for (const seg of getAllPathSegments(window.location.pathname)) paths.add(seg)
  for (const extra of extraPaths) {
    if (typeof extra !== 'string' || extra.length === 0) continue
    for (const seg of getAllPathSegments(extra)) paths.add(seg)
  }

  const expiredHeader = 'expires=Thu, 01 Jan 1970 00:00:00 UTC'
  for (const path of paths) {
    document.cookie = `${name}=; Max-Age=0; ${expiredHeader}; path=${path}; SameSite=Strict;`
  }
}
